/*

How to code a feature switch
============================
Decide on a feature switch id.

If code needs to behave differently, use has, eg if (has("my-feature")) {}

Never store the result or the id in a variable, so that the id can be searched for.

If code exists just for the feature (eg config or a property), comment it with the feature id.

Accompany everything with a thorough explanatory comment as to what's going on.


How to turn on and off for an individual
========================================
Visit any page of the site with a given query string to control feature switches, using Express's syntax for structured query parameters.

eg for a feature called "bob"
Visit https://www.sportinglife.com?feature[bob]=on

feature[bob]=on will turn it on
feature[bob]=off will turn it off

feature[bob]=reset (or indeed anything other than on or off) will reset it to the default value (ie what users see).

This places a session cookie on the client. So navigating to another page and refreshing will no affect the state of
the switch, but closing the browser will reset the feature to its default state.


How to turn on and off for all users
====================================
A feature must have an entry in `featureSpec` to work. User requests to an unexpected feature switch will fail silently.

By default, features are off. So the minimum for a spec is an empty object.

If a given feature should be "on" unless set by the query string, give it a `defaultValue` of "on" in featureSpec.
  eg
  const featureSpec = {
    'make-it-red': {
      defaultValue: "on"
    }
  };

If a given feature should be switchable for all users using an environment variable, prefix the variable name
with FEATURE_, and add the name to featureSpec (don't forget to update the env module to expose it).
  eg
  const featureSpec = {
    'make-it-red': {
      environmentVariable: "FEATURE_MAKE_IT_RED"
    }
  };

  or

  const featureSpec = {
    'make-it-red': {
      defaultValue: "on",
      environmentVariable: "FEATURE_MAKE_IT_RED"
    }
  };

An individual can still override this with the above query string switch, unless it is protected.

If a feature should be blocked in a given environment (eg prod), this is possible, with two stages to the process.

1. Ensure the envonment has a PROTECTABLE_FEATURES variable, set to "on"
2. add protected: true to the featureSpec
  eg

  const featureSpec = {
    'make-it-red': {
      protected: true
    }
  };

When you add, remove or update a feature switch, update the list on the Kepler page <https://kepler.bjss.com/display/SPL/Feature+Switches>.


*/
import cookie from 'react-cookie';
import env from 'env';
import config from 'config';
import LocalStorage from '../localStorage'

let localStorage = new LocalStorage();
export const setLocalStorageClient = client => {
  localStorage = client;
}

function getFeatureSpec() {
  return global.FEATURE_SPEC || {
    "integrated-betslip": {
      defaultValue: "on",
      environmentVariable: "FEATURE_INTEGRATED_BETSLIP"
    },
    "push-fast-results": {
      defaultValue: "on",
      environmentVariable: "FEATURE_PUSH_FAST_RESULTS"
    },
    "insider-benefits": {
      defaultValue: "on",
      environmentVariable: "FEATURE_INSIDER_BENEFITS"
    },
    "oddschecker-integration": {
      defaultValue: "canary",
      environmentVariable: "FEATURE_ODDSCHECKER_INTEGRATION"
    },
    "oddschecker-betslip": {
      defaultValue: "canary",
      environmentVariable: "FEATURE_ODDSCHECKER_BETSLIP"
    },
  }
}

function key(feature) {
  return `feature_${feature}`;
}

function defaultValue(spec) {
  return env[spec.environmentVariable] || spec.defaultValue;
}

export function has(feature) {
  const spec = getFeatureSpec()[feature] || {};
  let value;

  if (!config.isProduction && !getFeatureSpec().hasOwnProperty(feature)) {
    console.warn(`Feature "${feature}" has not been specified`); // eslint-disable-line no-console
  }

  if (spec.protected && config.protectableFeatures) {
    value = defaultValue(spec);
  }
  else {
    value = cookie.load(key(feature)) || defaultValue(spec);
  }

  if (value === "canary") {
    const canaryFeatures = localStorage.getValue('features') || [];
    value = canaryFeatures.includes(feature) ? "on" : "off"
  }

  return (value === "on");
}

export function setFeatures(features) {
  if (features) {
    Object.keys(features).forEach(function (feature) {
      const value = features[feature];
      
      switch(value) {
        case "on":
        case "off":
          if (getFeatureSpec().hasOwnProperty(feature)) {
            cookie.save(key(feature), value, {path: "/"});
          }
          break;
        default:
          cookie.remove(key(feature));
      }
    });
  }
}

export function getEnabledFeatures() {
  const features = [];
  Object.keys(getFeatureSpec()).forEach(feature => {
    if (has(feature)) {
      features.push(feature);
    }
  });

  return features;
}

export function isBetSlipEnabled(checkBetlinkEnabled = false) {
  if (!has("integrated-betslip")) {
    return false;
  }

  const oddscheckerEnabled = has('oddschecker-betslip') && config.oddschecker.enabled;
  if (oddscheckerEnabled && checkBetlinkEnabled) {
    return oddscheckerEnabled && config.oddschecker.betlinkEnabled;
  } 

  return oddscheckerEnabled;
}



// WEBPACK FOOTER //
// ./utils/features.js